import { useCallback, useEffect, useState } from "react"
import { DeviceOption } from "../../types/Common"
import { StateAction } from "../../types/State"
import { replaceStreamTrack } from "../../utils/media"
import { useAppContext, useAppDispatchContext } from "../Context"

type ReturnType = {
  isMicEnabled: boolean
  isCameraEnabled: boolean
  settingsOpen: boolean
  audioInputs: DeviceOption[]
  videoInputs: DeviceOption[]
  selectedAudioInput: string
  selectedVideoInput: string
  permissionsGranted: boolean
  handleSettingsClick: () => void
  handleSettingsClose: () => void
  handleAudioInputChange: (deviceId: string) => Promise<void>
  handleVideoInputChange: (deviceId: string) => Promise<void>
  toggleMicrophone: () => Promise<void>
  toggleCamera: () => Promise<void>
}

export const useDeviceAV = (): ReturnType => {
  const context = useAppContext()
  const dispatch = useAppDispatchContext()

  const [isMicEnabled, setIsMicEnabled] = useState<boolean>(
    context.audioEnabled ?? true,
  )
  const [isCameraEnabled, setIsCameraEnabled] = useState<boolean>(
    context.videoEnabled ?? true,
  )
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [audioInputs, setAudioInputs] = useState<DeviceOption[]>([])
  const [videoInputs, setVideoInputs] = useState<DeviceOption[]>([])
  const [permissionsGranted, setPermissionsGranted] = useState(false)

  const selectedAudioInput = context.audioDevice || ""
  const selectedVideoInput = context.videoDevice || ""

  // Sync local state with global context
  useEffect(() => {
    setIsMicEnabled(context.audioEnabled ?? true)
    setIsCameraEnabled(context.videoEnabled ?? true)
  }, [context.audioEnabled, context.videoEnabled])

  useEffect(() => {
    const getDevices = async () => {
      try {
        try {
          const probe = await navigator.mediaDevices.getUserMedia({
            audio: true,
            video: true,
          })
          probe.getTracks().forEach((track) => track.stop())
          setPermissionsGranted(true)
        } catch (permissionError) {
          setPermissionsGranted(false)
        }
        const devices = await navigator.mediaDevices.enumerateDevices()

        const audioDevices = devices
          .filter((device) => device.kind === "audioinput")
          .map((device) => ({
            label:
              device.label || `Microphone ${device.deviceId.slice(0, 5)}...`,
            deviceId: device.deviceId,
          }))

        const videoDevices = devices
          .filter((device) => device.kind === "videoinput")
          .map((device) => ({
            label: device.label || `Camera ${device.deviceId.slice(0, 5)}...`,
            deviceId: device.deviceId,
          }))

        setAudioInputs(audioDevices)
        setVideoInputs(videoDevices)

        if (audioDevices.length > 0 && !selectedAudioInput) {
          const defaultAudioDevice = audioDevices[0].deviceId
          dispatch({
            type: StateAction.setAudioDevice,
            payload: defaultAudioDevice,
          })

          // Apply to existing stream if available
          if (context.stream instanceof MediaStream) {
            try {
              const audioStream = await navigator.mediaDevices.getUserMedia({
                audio: { deviceId: { exact: defaultAudioDevice } },
              })
              replaceStreamTrack(
                context.stream,
                audioStream.getAudioTracks()[0],
                "audio",
              )
            } catch (e) {
              console.error(
                "Failed to apply default audio device to existing stream:",
                e,
              )
            }
          }
        }

        if (videoDevices.length > 0 && !selectedVideoInput) {
          const defaultVideoDevice = videoDevices[0].deviceId
          dispatch({
            type: StateAction.setVideoDevice,
            payload: defaultVideoDevice,
          })

          // Apply to existing stream if available
          if (context.stream instanceof MediaStream) {
            try {
              const videoStream = await navigator.mediaDevices.getUserMedia({
                video: { deviceId: { exact: defaultVideoDevice } },
              })
              replaceStreamTrack(
                context.stream,
                videoStream.getVideoTracks()[0],
                "video",
              )
            } catch (e) {
              console.error(
                "Failed to apply default video device to existing stream:",
                e,
              )
            }
          }
        }
      } catch (error) {
        console.error("Error getting media devices:", error)
      }
    }

    getDevices()
  }, [dispatch, selectedAudioInput, selectedVideoInput])

  const toggleMicrophone = useCallback(async () => {
    try {
      // The reducer only toggles when a track exists, so the hook must too or the two states diverge
      if (!(context.stream instanceof MediaStream)) return
      const audioTracks = context.stream.getAudioTracks()
      if (audioTracks.length === 0) return

      const enabled = !isMicEnabled
      audioTracks.forEach((track) => {
        track.enabled = enabled
      })

      setIsMicEnabled(enabled)
      dispatch({ type: StateAction.toggleAudio })
    } catch (e) {
      console.error("Failed to toggle microphone:", e)
    }
  }, [context.stream, isMicEnabled, dispatch])

  const toggleCamera = useCallback(async () => {
    try {
      if (!(context.stream instanceof MediaStream)) return
      const videoTracks = context.stream.getVideoTracks()
      if (videoTracks.length === 0) return

      const enabled = !isCameraEnabled
      videoTracks.forEach((track) => {
        track.enabled = enabled
      })

      setIsCameraEnabled(enabled)
      dispatch({ type: StateAction.toggleVideo })
    } catch (e) {
      console.error("Failed to toggle camera:", e)
    }
  }, [context.stream, isCameraEnabled, dispatch])

  const handleSettingsClick = useCallback(() => {
    setSettingsOpen(true)
  }, [])

  const handleSettingsClose = useCallback(() => {
    setSettingsOpen(false)
  }, [])

  const handleAudioInputChange = useCallback(
    async (deviceId: string) => {
      try {
        const audioStream = await navigator.mediaDevices.getUserMedia({
          audio: { deviceId: { exact: deviceId } },
        })

        const mediaStream =
          context.stream instanceof MediaStream
            ? context.stream
            : new MediaStream()

        replaceStreamTrack(
          mediaStream,
          audioStream.getAudioTracks()[0],
          "audio",
        )

        dispatch({ type: StateAction.setStream, payload: mediaStream })
        dispatch({ type: StateAction.setAudioDevice, payload: deviceId })
        setIsMicEnabled(true)
      } catch (e) {
        console.error("Failed to switch audio device:", e)
      }
    },
    [context.stream, dispatch],
  )

  const handleVideoInputChange = useCallback(
    async (deviceId: string) => {
      try {
        const videoStream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { exact: deviceId } },
        })

        const mediaStream =
          context.stream instanceof MediaStream
            ? context.stream
            : new MediaStream()

        replaceStreamTrack(
          mediaStream,
          videoStream.getVideoTracks()[0],
          "video",
        )

        dispatch({ type: StateAction.setStream, payload: mediaStream })
        dispatch({ type: StateAction.setVideoDevice, payload: deviceId })
        setIsCameraEnabled(true)
      } catch (e) {
        console.error("Failed to switch video device:", e)
      }
    },
    [context.stream, dispatch],
  )

  return {
    isMicEnabled,
    isCameraEnabled,
    settingsOpen,
    audioInputs,
    videoInputs,
    selectedAudioInput,
    selectedVideoInput,
    permissionsGranted,
    handleSettingsClick,
    handleSettingsClose,
    handleAudioInputChange,
    handleVideoInputChange,
    toggleMicrophone,
    toggleCamera,
  }
}
